import { defaultConfig } from "./defaultQuickSteps";
import { initializeQuickStepsMutator } from "../actions/initializeQuickSteps";
import { lazyReadOcsSettings } from "owa-ocs-service-gql";
import type { OcsResponseItem } from "owa-graph-schema";
import { QUICK_STEPS_SETTING_NAME } from "../constants";
import type { QuickStepsConfig } from "../store/schema/QuickStepsConfig";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import saveQuickStepsToService from "./saveQuickStepsToService";
import type { MailboxInfo } from "owa-client-types";
import { type QuickStepHotkeyId } from "owa-quick-step-types";
import type { QuickStepFolderType, QuickStepCategoryType } from "owa-quick-step-types";
import { logUsage } from "owa-analytics";
import { getAccountScopeUserSettings } from "owa-session-store";
import { QuickStepsLoadState } from "../store/schema/QuickStepsLoadState";
import { getQuickStepsLoadState } from "../selectors/getQuickStepsLoadState";
import { setQuickStepsLoadStateMutator } from "../actions/setQuickStepsLoadState";
import { getGlobalSettingsAccountMailboxInfo, type AccountSource, } from "owa-account-source-list-store";
import { OnlyMailStateChanges, RegisterForAccountStateChange } from "owa-account-source-list";
import { isServiceRequestSupportedForMailbox } from "owa-service/lib/utils/isServiceRequestSupportedForMailbox";

export function loadQuickSteps(): void {
    loadMailboxQuickSteps(getGlobalSettingsAccountMailboxInfo());
    RegisterForAccountStateChange((accountSource: AccountSource) => {
        if (isServiceRequestSupportedForMailbox(accountSource.mailboxInfo)) {
            loadMailboxQuickSteps(accountSource.mailboxInfo);
        }
    }, (_accountSource: AccountSource) => { }, // no-op the pre-account removed callback
    OnlyMailStateChanges);
}

export async function loadMailboxQuickSteps(mailboxInfo: MailboxInfo): Promise<void> {
    // Keep track of requests so we don't send multiple
    const loadingState = getQuickStepsLoadState(mailboxInfo);
    if (loadingState === QuickStepsLoadState.Loading ||
        loadingState === QuickStepsLoadState.Loaded) {
        return;
    }
    
    setQuickStepsLoadStateMutator(mailboxInfo, QuickStepsLoadState.Loading);
    
    if (isGlobalFeatureEnabled("tri-quickSteps-reset")) {
        hardResetQuickSteps(mailboxInfo);
    }
    else {
        // Retrieve the quick step value from prime settings
        const primeSettings = getAccountScopeUserSettings(mailboxInfo)?.PrimeSettings;
        let json = primeSettings?.Items?.filter(item => item.Id === "Mail_QuickSteps").map(item => item.Value?.[0]?.value)[0];
        
        //If it's not in the prime settings for some reason, fall back to OCS
        if (!json) {
            const ocsResponse: OcsResponseItem[] = await lazyReadOcsSettings.importAndExecute(mailboxInfo, "ACCOUNT", [QUICK_STEPS_SETTING_NAME]);
            
            json = ocsResponse[0]?.value;
        }
        let quickStepsConfig: QuickStepsConfig;
        if (json) {
            const data = JSON.parse(json);
            if (data.version > defaultConfig.version) {
                quickStepsConfig = defaultConfig;
                quickStepsConfig.errorState = true;
            }
            else {
                const areActionsModified = validateQuickStepsData(data, mailboxInfo);
                quickStepsConfig = data;
                quickStepsConfig.errorState = false;
                if (areActionsModified) {
                    const configString = JSON.stringify(quickStepsConfig);
                    saveQuickStepsToService(mailboxInfo, configString);
                }
            }
        }
        else {
            quickStepsConfig = defaultConfig;
        }
        initializeQuickStepsMutator(mailboxInfo, quickStepsConfig as QuickStepsConfig);
    }
}

function hardResetQuickSteps(mailboxInfo: MailboxInfo) {
    initializeQuickStepsMutator(mailboxInfo, defaultConfig);
    const configString = JSON.stringify(defaultConfig);
    saveQuickStepsToService(mailboxInfo, configString);
}

function validateQuickStepsData(config: QuickStepsConfig, mailboxInfo: MailboxInfo): boolean {
    let areActionsModified = false;
    let categoryObjectBugFixed = true;
    config.quickSteps.forEach(step => {
        // we have removed hotkey zero as an option
        // if the user has that, set it to undefined
        if (step.hotkey === 5) {
            step.hotkey = undefined;
        }
        step.actions.forEach(action => {
            if ((action.type === "moveTo" || action.type == "copyTo") &&
                action.extraInfo != undefined) {
                const folder = action.extraInfo as QuickStepFolderType;
                if (folder.folderId === undefined && folder.isAlwaysAsk === undefined) {
                    // we should save folderId data from extraInfo string into the folderId field of QuickStepFolderType
                    const newFolder: QuickStepFolderType = {
                        folderId: action.extraInfo as string,
                        isAlwaysAsk: false,
                    };
                    action.extraInfo = newFolder;
                    areActionsModified = true;
                }
            }
            if (action.type === "category" && action.extraInfo != undefined) {
                if (typeof action.extraInfo === "string") {
                    const newCategory: QuickStepCategoryType = {
                        categoryId: action.extraInfo as string,
                        isAlwaysAsk: false,
                    };
                    action.extraInfo = newCategory;
                    areActionsModified = true;
                }
                else if (typeof action.extraInfo === "object") {
                    categoryObjectBugFixed = false;
                    const category = action.extraInfo as QuickStepCategoryType;
                    if (category?.categoryId !== null && category?.categoryId !== undefined) {
                        if (typeof category.categoryId === "string" &&
                            category.isAlwaysAsk !== undefined) {
                            const newCategory: QuickStepCategoryType = {
                                categoryId: category.categoryId,
                                isAlwaysAsk: category.isAlwaysAsk,
                            };
                            action.extraInfo = newCategory;
                        }
                        else if (typeof category.categoryId === "object") {
                            // This is a case of fixing up incorrectly saved data on edit of a quick step,
                            // where we accidentally saved the category object instead of the categoryId string
                            // Setting return value areActionsModified to true fixes this up for subsequent loads
                            const categoryObject = category.categoryId as QuickStepCategoryType;
                            if (categoryObject !== null &&
                                categoryObject["categoryId"] !== null &&
                                categoryObject["categoryId"] !== undefined) {
                                if (typeof categoryObject["categoryId"] === "string") {
                                    const newCategory: QuickStepCategoryType = {
                                        categoryId: categoryObject["categoryId"],
                                        isAlwaysAsk: false,
                                    };
                                    action.extraInfo = newCategory;
                                    areActionsModified = true;
                                    categoryObjectBugFixed = true;
                                }
                                // If the categoryId is still an object, we will try to extract the string value
                                // till one more layer of fix is applied
                                else if (typeof categoryObject["categoryId"] === "object") {
                                    categoryObjectBugFixed = false;
                                    // If the categoryId is an object, extract the string value
                                    const categoryObjectInner = categoryObject["categoryId"] as QuickStepCategoryType;
                                    if (categoryObjectInner &&
                                        categoryObjectInner["categoryId"] !== undefined &&
                                        categoryObjectInner["categoryId"] !== null &&
                                        typeof categoryObjectInner["categoryId"] === "string") {
                                        const newCategoryInner: QuickStepCategoryType = {
                                            categoryId: categoryObjectInner["categoryId"],
                                            isAlwaysAsk: false,
                                        };
                                        action.extraInfo = newCategoryInner;
                                        areActionsModified = true;
                                        categoryObjectBugFixed = true;
                                    }
                                }
                                else {
                                    // If the categoryId is not a string, we don't know what to do with it,
                                    // so lot telemetry values to track this case
                                    categoryObjectBugFixed = false;
                                }
                            }
                        }
                    }
                }
            }
        });
    });
    // We have introduced a new runOnce flag to make the Quick Steps UI
    // to appear for new users and anyone that has imported Quick Steps from Win32
    // We don't want to show the UI for existing OWA users that already have a config
    if (config.runOnce === undefined) {
        config.runOnce = true;
        areActionsModified = true;
    }
    // Log the usage of Quick Steps category error fix applied
    
    logUsage("QuickSteps_CategoryError_ActionModified", mailboxInfo, {
        areActionsModified,
        categoryObjectBugFixed,
    });
    return areActionsModified;
}
